import { Controller } from "@hotwired/stimulus"
import { createPicker } from "picmo"
import faFreeIcons from "../icon_picker/fa_free_icons.json"

// We only offer solid/regular in the UI, but keep fa-brands for backward compatibility
// in case existing stored values include it.
const FA_STYLE_TOKENS = new Set(["fa-solid", "fa-solid", "fa-brands"])
const COLOR_NAMES = ["blue", "indigo", "purple", "pink", "red", "orange", "yellow", "green", "teal", "cyan"]

function normalizeTokens(value) {
  return (value || "")
    .toString()
    .trim()
    .split(/\s+/)
    .map((t) => t.trim())
    .filter(Boolean)
}

function parseIconValue(value) {
  const tokens = normalizeTokens(value)
  const colorTokens = tokens.filter((t) => t.startsWith("color-"))
  const otherTokens = tokens.filter((t) => !t.startsWith("color-"))

  const color = colorTokens.length ? colorTokens[0] : null

  const faStyle = otherTokens.find((t) => FA_STYLE_TOKENS.has(t)) || "fa-solid"
  const faIcon =
    otherTokens.find((t) => t !== "fa" && t.startsWith("fa-") && !FA_STYLE_TOKENS.has(t)) || null

  // Emoji: exactly one non-color token and not a font-awesome icon token.
  const emoji = !faIcon && otherTokens.length === 1 ? otherTokens[0] : null

  return { tokens, color, faStyle, faIcon, emoji }
}

function buildValue({ color, faStyle, faIcon, emoji }) {
  const out = []
  if (color) out.push(color)
  if (emoji) out.push(emoji)
  if (faIcon) out.push(faStyle || "fa-solid", faIcon)
  return out.join(" ").trim()
}

export default class extends Controller {
  static targets = [
    "input",
    "preview",
    "toggleButton",
    "popover",
    "tabButtonEmoji",
    "tabButtonIcons",
    "panelEmoji",
    "panelIcons",
    "emojiContainer",
    "emojiSearch",
    "iconSearch",
    "faStyle",
    "iconResults",
    "colorButtons",
  ]

  connect() {
    this._onDocClick = this._handleDocumentClick.bind(this)
    this._onKeyDown = this._handleKeyDown.bind(this)
    document.addEventListener("click", this._onDocClick, true)
    document.addEventListener("keydown", this._onKeyDown, true)

    // State
    this._parsed = parseIconValue(this.inputTarget?.value)
    this._icons = Array.isArray(faFreeIcons) ? faFreeIcons : []
    this._tab = this._defaultTabForParsed(this._parsed)
    this._faSupportProbe = null
    this._uiFaStyle = this._parsed?.faStyle || "fa-solid"

    this._setupTabs()
    this._setupColors()
    this._setupEmojiPicker()
    this._setupIconResults()
    this._syncFromInput()
  }

  disconnect() {
    document.removeEventListener("click", this._onDocClick, true)
    document.removeEventListener("keydown", this._onKeyDown, true)
    this._onDocClick = null
    this._onKeyDown = null

    if (this._onIconClick && this.hasIconResultsTarget) {
      this.iconResultsTarget.removeEventListener("click", this._onIconClick)
      this._onIconClick = null
    }
    if (this._onColorClick && this.hasColorButtonsTarget) {
      this.colorButtonsTarget.removeEventListener("click", this._onColorClick)
      this._onColorClick = null
    }

    if (this.picker) {
      try {
        this.picker.destroy()
      } catch (_) {
        // ignore
      }
      this.picker = null
    }

    // _faSupportProbe is no longer used (kit uses SVG replacement); keep for compatibility if present.
    if (this._faSupportProbe) {
      this._faSupportProbe.remove()
      this._faSupportProbe = null
    }
  }

  // UI actions
  toggle(event) {
    event?.preventDefault?.()
    if (this._isOpen()) this._close()
    else this._open()
  }

  close(event) {
    event?.preventDefault?.()
    this._close()
  }

  clear(event) {
    event?.preventDefault?.()
    this._setValue("")
    this._syncFromInput()
    this._close()
  }

  showEmojiTab(event) {
    event?.preventDefault?.()
    this._showTab("emoji")
  }

  showIconsTab(event) {
    event?.preventDefault?.()
    this._showTab("icons")
  }

  // Keyboard nav for tabs (Left/Right/Home/End)
  tabKeyDown(event) {
    const keys = ["ArrowLeft", "ArrowRight", "Home", "End"]
    if (!keys.includes(event.key)) return
    event.preventDefault()

    const order = ["emoji", "icons"]
    const currentIdx = Math.max(0, order.indexOf(this._tab))
    let nextIdx = currentIdx

    if (event.key === "ArrowLeft") nextIdx = (currentIdx + order.length - 1) % order.length
    if (event.key === "ArrowRight") nextIdx = (currentIdx + 1) % order.length
    if (event.key === "Home") nextIdx = 0
    if (event.key === "End") nextIdx = order.length - 1

    const nextTab = order[nextIdx]
    this._showTab(nextTab)

    const btn =
      nextTab === "emoji"
        ? this.tabButtonEmojiTarget
        : nextTab === "icons"
          ? this.tabButtonIconsTarget
          : null
    btn?.focus?.()
  }

  inputChanged() {
    this._syncFromInput()
  }

  emojiSearchChanged() {
    // We rely on PicMo's built-in search UI; hide our external search input.
    // Keeping this action avoids JS errors if the input exists.
  }

  iconSearchChanged() {
    this._renderIconResults()
  }

  faStyleChanged() {
    // Kept for backward compatibility (old <select> binding). Treat current
    // target value as the UI style and re-render.
    this._uiFaStyle = (this.hasFaStyleTarget ? this.faStyleTarget.value : null) || this._uiFaStyle || "fa-solid"
    this._syncFaStyleButtons()
    this._renderIconResults()
    this._updatePreview()
  }

  setFaStyle(event) {
    const btn = event.target?.closest?.("button[data-fa-style]")
    if (!btn) return
    event.preventDefault()
    const style = btn.dataset.faStyle || "fa-solid"
    this._uiFaStyle = style
    if (this.hasFaStyleTarget) this.faStyleTarget.value = style
    this._syncFaStyleButtons()
    this._renderIconResults()
    this._updatePreview()
  }

  // Setup helpers
  _setupTabs() {
    this._showTab(this._tab)
  }

  _setupColors() {
    if (!this.hasColorButtonsTarget) return
    this.colorButtonsTarget.innerHTML = ""

    const makeBtn = (label, value) => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "icon-picker__color-swatch"
      btn.dataset.colorValue = value || ""
      btn.setAttribute("aria-label", label)
      return btn
    }

    const noneBtn = makeBtn("None", "")
    noneBtn.classList.add("icon-picker__color-swatch--none")
    this.colorButtonsTarget.appendChild(noneBtn)
    COLOR_NAMES.forEach((name) => {
      const token = `color-${name}`
      const btn = makeBtn(name, token)
      btn.classList.add("icon-picker__color-swatch--swatch")
      btn.dataset.swatch = name
      this.colorButtonsTarget.appendChild(btn)
    })

    this._onColorClick = (e) => {
      const btn = e.target?.closest?.("button[data-color-value]")
      if (!btn) return
      e.preventDefault()
      const token = btn.dataset.colorValue || ""
      this._setColor(token || null)
    }

    this.colorButtonsTarget.addEventListener("click", this._onColorClick)
  }

  _setupEmojiPicker() {
    if (!this.hasEmojiContainerTarget) return
    // Hide our external emoji search input; PicMo renders its own search when enabled.
    if (this.hasEmojiSearchTarget) this.emojiSearchTarget.closest(".icon-picker__search-row")?.classList.add("d-none")

    try {
      this.picker = createPicker({
        rootElement: this.emojiContainerTarget,
        theme: "auto",
        showSearch: true,
        showRecents: false,
        showPreview: false,
      })

      this.picker.addEventListener("emoji:select", (selection) => {
        const emoji = selection?.emoji?.toString?.() || ""
        if (!emoji) return
        this._selectEmoji(emoji)
      })
    } catch (_) {
      // If PicMo fails, keep the UI but don't crash the page.
      this.emojiContainerTarget.innerHTML =
        '<div class="small text-body-secondary p-2">Emoji picker failed to load.</div>'
      this.picker = null
    }
  }

  _setupIconResults() {
    if (!this.hasIconResultsTarget) return

    this._onIconClick = (e) => {
      const btn = e.target?.closest?.("button[data-fa-icon]")
      if (!btn) return
      e.preventDefault()
      const icon = btn.dataset.faIcon || ""
      if (!icon) return
      this._selectFaIcon(icon)
    }

    this.iconResultsTarget.addEventListener("click", this._onIconClick)
    this._renderIconResults()
  }

  _faPrefixForStyleClass(styleClass) {
    if (styleClass === "fa-solid") return "fas"
    if (styleClass === "fa-solid") return "far"
    if (styleClass === "fa-brands") return "fab"
    return "fas"
  }

  _faIconNameFromClass(iconClass) {
    return (iconClass || "").toString().replace(/^fa-/, "")
  }

  _faIconSupported({ styleClass, iconClass }) {
    // With Font Awesome Kit, prefer querying the icon definitions rather than
    // relying on webfont ::before content (kit often uses SVG replacement).
    const fa = window.FontAwesome
    const find = fa?.findIconDefinition
    if (typeof find === "function") {
      const prefix = this._faPrefixForStyleClass(styleClass)
      const iconName = this._faIconNameFromClass(iconClass)
      try {
        return !!find({ prefix, iconName })
      } catch (_) {
        return true
      }
    }
    return true
  }

  _refreshFontAwesomeWithin(node) {
    try {
      window.FontAwesome?.dom?.i2svg?.({ node })
    } catch (_) {
      // ignore
    }
  }

  _renderIconResults() {
    if (!this.hasIconResultsTarget) return
    const q = (this.hasIconSearchTarget ? this.iconSearchTarget.value : "").toString().trim().toLowerCase()

    const style =
      (this.hasFaStyleTarget ? this.faStyleTarget.value : null) || this._uiFaStyle || "fa-solid"

    const results = []
    const limit = 240
    for (let i = 0; i < this._icons.length; i += 1) {
      const icon = (this._icons[i] || "").toString()
      if (!icon) continue
      if (q && !icon.toLowerCase().includes(q)) continue
      if (!this._faIconSupported({ styleClass: style, iconClass: icon })) continue
      results.push(icon)
      if (results.length >= limit) break
    }

    const current = this._parsed?.faIcon

    const frag = document.createDocumentFragment()
    results.forEach((icon) => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "icon-picker__icon-btn"
      if (current && icon === current) btn.classList.add("active")
      btn.dataset.faIcon = icon
      btn.title = icon.replace(/^fa-/, "")
      btn.setAttribute("aria-label", icon.replace(/^fa-/, ""))

      const iEl = document.createElement("i")
      iEl.className = `${style} ${icon}`
      iEl.setAttribute("aria-hidden", "true")

      btn.appendChild(iEl)
      const sr = document.createElement("span")
      sr.className = "visually-hidden"
      sr.textContent = icon.replace(/^fa-/, "")
      btn.appendChild(sr)
      frag.appendChild(btn)
    })

    this.iconResultsTarget.innerHTML = ""
    this.iconResultsTarget.appendChild(frag)
    this._refreshFontAwesomeWithin(this.iconResultsTarget)
  }

  // State sync
  _syncFromInput({ preferExistingFa = false } = {}) {
    const parsed = parseIconValue(this.inputTarget.value)
    this._parsed = parsed

    // If the stored value explicitly includes a style token, sync the UI style to it.
    // Otherwise keep whatever the user last chose in the UI.
    if (parsed.tokens.includes("fa-solid") || parsed.tokens.includes("fa-solid") || parsed.tokens.includes("fa-brands")) {
      this._uiFaStyle = parsed.faStyle || this._uiFaStyle || "fa-solid"
    } else if (preferExistingFa && this.hasFaStyleTarget && this.faStyleTarget.value) {
      this._uiFaStyle = this.faStyleTarget.value
    }

    if (this.hasFaStyleTarget) this.faStyleTarget.value = this._uiFaStyle || "fa-solid"

    this._syncFaStyleButtons()
    this._updatePreview()
    this._updateColorUI()
    this._applyColorToIconGrid()
    this._renderIconResults()
  }

  _syncFaStyleButtons() {
    const root = this.hasPopoverTarget ? this.popoverTarget : this.element
    const current = this.hasFaStyleTarget ? this.faStyleTarget.value : "fa-solid"
    root.querySelectorAll("button[data-fa-style]").forEach((btn) => {
      btn.classList.toggle("active", (btn.dataset.faStyle || "") === current)
      btn.setAttribute("aria-pressed", (btn.dataset.faStyle || "") === current ? "true" : "false")
    })
  }

  _updatePreview() {
    if (!this.hasPreviewTarget) return

    // Remove previous color-* classes.
    this.previewTarget.className = this.previewTarget.className
      .split(/\s+/)
      .filter((c) => !c.startsWith("color-"))
      .join(" ")
      .trim()

    if (this._parsed.color) this.previewTarget.classList.add(this._parsed.color)

    if (this._parsed.emoji) {
      this.previewTarget.classList.add("page-icon--emoji")
      this.previewTarget.classList.remove("page-icon--fa")
      this.previewTarget.textContent = this._parsed.emoji
      return
    }

    if (this._parsed.faIcon) {
      let style =
        this._parsed.faStyle ||
        (this.hasFaStyleTarget ? this.faStyleTarget.value : null) ||
        this._uiFaStyle ||
        "fa-solid"

      // If we have a mismatched style/icon (e.g. a brand icon stored with fa-solid),
      // try to render it with brands as a fallback, but do not rewrite the stored value.
      if (!this._faIconSupported({ styleClass: style, iconClass: this._parsed.faIcon })) {
        if (this._faIconSupported({ styleClass: "fa-brands", iconClass: this._parsed.faIcon })) {
          style = "fa-brands"
        }
      }
      this.previewTarget.classList.add("page-icon--fa")
      this.previewTarget.classList.remove("page-icon--emoji")
      this.previewTarget.innerHTML = `<i class="${style} ${this._parsed.faIcon}"></i>`
      this._refreshFontAwesomeWithin(this.previewTarget)
      return
    }

    // Empty: show a subtle placeholder.
    this.previewTarget.classList.remove("page-icon--fa", "page-icon--emoji")
    this.previewTarget.textContent = "—"
  }

  _updateColorUI() {
    if (!this.hasColorButtonsTarget) return
    const current = this._parsed.color || ""
    this.colorButtonsTarget.querySelectorAll("button[data-color-value]").forEach((btn) => {
      const v = btn.dataset.colorValue || ""
      btn.classList.toggle("active", v === current)
    })
  }

  _applyColorToIconGrid() {
    if (!this.hasIconResultsTarget) return
    // Remove previous color-* classes.
    this.iconResultsTarget.className = this.iconResultsTarget.className
      .split(/\s+/)
      .filter((c) => !c.startsWith("color-"))
      .join(" ")
      .trim()

    if (this._parsed.color) this.iconResultsTarget.classList.add(this._parsed.color)
  }

  _selectEmoji(emoji) {
    this._parsed = parseIconValue(this.inputTarget.value)
    const next = buildValue({ color: this._parsed.color, emoji })
    this._setValue(next)
    this._syncFromInput()
    this._close()
  }

  _selectFaIcon(iconToken) {
    this._parsed = parseIconValue(this.inputTarget.value)
    const style =
      (this.hasFaStyleTarget ? this.faStyleTarget.value : null) ||
      this._uiFaStyle ||
      this._parsed.faStyle ||
      "fa-solid"
    const next = buildValue({ color: this._parsed.color, faStyle: style, faIcon: iconToken })
    this._setValue(next)
    this._syncFromInput()
    this._close()
  }

  _setColor(colorTokenOrNull) {
    this._parsed = parseIconValue(this.inputTarget.value)
    const next = buildValue({
      color: colorTokenOrNull,
      emoji: this._parsed.emoji,
      faStyle: this._parsed.faStyle,
      faIcon: this._parsed.faIcon,
    })
    this._setValue(next)
    this._syncFromInput()
  }

  _setValue(value) {
    if (!this.hasInputTarget) return
    this.inputTarget.value = value
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // Popover behavior
  _isOpen() {
    return this.hasPopoverTarget && !this.popoverTarget.hasAttribute("hidden")
  }

  _open() {
    if (!this.hasPopoverTarget) return
    this._tab = this._defaultTabForParsed(parseIconValue(this.inputTarget.value))
    this._showTab(this._tab)
    this.popoverTarget.removeAttribute("hidden")
    if (this.hasToggleButtonTarget) this.toggleButtonTarget.setAttribute("aria-expanded", "true")
    // Focus first meaningful control.
    this._focusPrimaryControlForTab(this._tab)
  }

  _close() {
    if (!this.hasPopoverTarget) return
    this.popoverTarget.setAttribute("hidden", "")
    if (this.hasToggleButtonTarget) this.toggleButtonTarget.setAttribute("aria-expanded", "false")
  }

  _handleDocumentClick(event) {
    if (!this._isOpen()) return
    if (this.element.contains(event.target)) return
    this._close()
  }

  _handleKeyDown(event) {
    if (!this._isOpen()) return
    if (event.key === "Escape") {
      event.preventDefault()
      event.stopPropagation()
      this._close()
      this.toggleButtonTarget?.focus?.()
    }
  }

  _defaultTabForParsed(parsed) {
    if (parsed?.faIcon) return "icons"
    if (parsed?.emoji) return "emoji"
    return "emoji"
  }

  _focusPrimaryControlForTab(which) {
    // Defer to ensure PicMo / DOM has rendered.
    window.requestAnimationFrame(() => {
      if (!this._isOpen()) return

      if (which === "icons") {
        this.iconSearchTarget?.focus?.()
        return
      }

      // Emoji: PicMo provides its own search box.
      const picmoSearch =
        this.emojiContainerTarget?.querySelector?.("input[type='search'], input[role='searchbox']")
      picmoSearch?.focus?.()
    })
  }

  _showTab(which) {
    this._tab = which

    const setActive = (btn, active) => {
      if (!btn) return
      btn.classList.toggle("active", active)
      btn.setAttribute("aria-selected", active ? "true" : "false")
      btn.setAttribute("tabindex", active ? "0" : "-1")
    }

    setActive(this.tabButtonEmojiTarget, which === "emoji")
    setActive(this.tabButtonIconsTarget, which === "icons")

    const setPanelVisible = (panel, visible) => {
      if (!panel) return
      panel.classList.toggle("d-none", !visible)
      panel.toggleAttribute("hidden", !visible)
      panel.setAttribute("aria-hidden", visible ? "false" : "true")
    }

    setPanelVisible(this.panelEmojiTarget, which === "emoji")
    setPanelVisible(this.panelIconsTarget, which === "icons")

    if (this._isOpen()) this._focusPrimaryControlForTab(which)
  }
}
